import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import TestCard from '../../../../../components/pages/Register/PassTheTest/TestCard'
import Button from '../../../../../components/UI/Button'
import TestResultCard from '../../../../../components/pages/Register/PassTheTest/TestResultCard'
import MyClassCategoriesTestInstructions from './MyClassCategoriesTestInstructions'
import MyClassCategoriesTestProcess from './MyClassCategoriesTestProcess'
import MyClassCategoriesTestResults from './MyClassCategoriesTestResults'

import {
  selectIsLoading,
  selectPassedTestsCreateBanner,
  selectRole,
} from '../../../../../routing/fetchingfunction/user/user.reducer'
import {
  getTutorTestRecords,
  setSelectedCategories,
} from '../../../../../routing/fetchingfunction/user/user.action'

import { useAvailableCategories, useMainCategories } from '../../../../../hooks'

import { tutorRegistrationTests } from '../../../../../constants'
import moment from 'moment/moment'

const MyClassCategories = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { lastPassedCategory } = useSelector((state) => state.user)
  const { takenTests } = useAvailableCategories()

  const isLoading = useSelector(selectIsLoading)
  const role = useSelector(selectRole)

  const mainCategories = useMainCategories()

  const [selectedTests, setSelectedTests] = useState([])
  const [testStep, setTestStep] = useState(1)
  const [testPassedStatus, setTestPassedStatus] = useState(null)

  const passedTestsCount = takenTests.filter((passedTest) => passedTest.test?.level).length || 0

  const handleSelect = (test) => {
    setSelectedTests((prevSelectedTests) => {
      if (prevSelectedTests.findIndex((selectedTest) => selectedTest.id === test.id) === -1) {
        return [...prevSelectedTests, test]
      }

      return prevSelectedTests.filter((prevSelectedTest) => prevSelectedTest.id !== test.id)
    })
  }

  const handleSubmitSelectedCategories = () => {
    dispatch(setSelectedCategories(selectedTests))
    setTestStep((prevTestStep) => prevTestStep + 1)
  }

  const handleSubmitInstructions = () => {
    setTestStep(3)
    navigate(`/${role}/settings/my-class-categories/test-process`)
  }

  const handleSubmitTests = (testPassedStatus) => {
    setTestPassedStatus(testPassedStatus)
    setTestStep(4)
  }

  const handleCancelTest = (testPassedStatus) => {
    if (testPassedStatus) {
      setTestPassedStatus(testPassedStatus)
      setTestStep(4)
    } else {
      navigate(`/${role}/settings/my-class-categories`)
      setTestStep(1)
    }
  }

  const handleCancelTestResults = () => {
    setSelectedTests([])
    handleCancelTest()
  }

  const handleContinueTest = (nextStepValue) => {
    if (nextStepValue === 1) {
      setSelectedTests([])
      navigate(`/${role}/settings/my-banners`)
    } else {
      setTestStep((prevTestStep) => prevTestStep - 1)
    }
  }

  useEffect(() => {
    if (testStep === 1 && !location.pathname.endsWith('categories')) {
      handleCancelTest()
    }
  }, [])

  const testResultCardsContent = mainCategories.map((tutorRegistrationTest) => {
    const tutorRegistrationTestFound = tutorRegistrationTests.find(
      (test) => test.slug === tutorRegistrationTest.slug
    )
    const icon = tutorRegistrationTestFound?.icon
    const description = tutorRegistrationTestFound?.description
    const passedTest = takenTests.find(
      (passedTest) => passedTest.category.slug === tutorRegistrationTest.slug
    )
    const nextAvailableDate = passedTest?.test?.next_available_date
    let isTestPassed = !!passedTest

    if (nextAvailableDate) {
      const now = moment()

      const nextAvailableDay = moment(nextAvailableDate)

      const milliseconds = nextAvailableDay.diff(now, 'milliseconds')

      if (milliseconds <= 0) {
        isTestPassed = false
      }
    }

    return isTestPassed ? (
      <TestResultCard
        key={tutorRegistrationTest.id}
        id={tutorRegistrationTest.id}
        title={tutorRegistrationTest.name}
        description={description}
        isPassed={passedTest?.test?.is_passed}
        level={passedTest.test?.level}
        result={passedTest?.test?.result}
        nextAvailableDate={passedTest.test?.next_available_date}
        icon={icon}
        className="!p-[24px]"
      />
    ) : (
      <TestCard
        key={tutorRegistrationTest.id}
        id={tutorRegistrationTest.id}
        title={tutorRegistrationTest.name}
        description={description}
        icon={icon}
        onSelect={handleSelect}
        slug={tutorRegistrationTest.slug}
        name={tutorRegistrationTest.name}
        isSelected={
          selectedTests.findIndex(
            (selectedTest) => tutorRegistrationTest.id === selectedTest.id
          ) !== -1
        }
      />
    )
  })

  return (
    <div className="w-full pb-16">
      {(testStep === 1 || testStep === 2) && (
        <>
          <h3 className="text-2xl font-bold rounded-lg bg-[#FEEBDD] p-[20px] text-primary-100">
            My Class Categories Settings
          </h3>

          <div className="p-[32px] rounded-[10px] bg-[#FEEBDD] mt-[20px] w-full flex flex-col">
            <h4 className="text-base font-bold text-primary-100">Class Category</h4>

            <h5 className="mt-[8px] text-sm font-normal text-primary-100 mb-[24px]">
              You have passed {passedTestsCount} category test{passedTestsCount > 1 ? 's' : ''}.
              Select and start a category test to open more classes.
            </h5>

            <div className="text-[#F67443] w-full grid grid-cols-3 grid-rows-3 gap-[10px]">
              {testResultCardsContent}
            </div>

            <Button
              className="w-fit mt-[24px] mx-auto font-semibold"
              onClick={handleSubmitSelectedCategories}
              isLoading={isLoading}
              disabled={!selectedTests.length}
            >
              Start a Category Test
            </Button>
          </div>
        </>
      )}

      <MyClassCategoriesTestInstructions
        open={testStep === 2}
        onClose={handleCancelTest}
        onSubmit={handleSubmitInstructions}
      />

      {testStep === 3 && (
        <MyClassCategoriesTestProcess onSubmit={handleSubmitTests} onCancel={handleCancelTest} />
      )}

      {testStep === 4 && (
        <MyClassCategoriesTestResults
          passedStatus={testPassedStatus}
          testName={lastPassedCategory?.title}
          onContinue={handleContinueTest}
          onCancel={handleCancelTestResults}
        />
      )}
    </div>
  )
}

export default MyClassCategories
